// ============================================================================
// 🏠 COMPONENTE PRINCIPAL - FINANCEIRO PAGE — V18 FINAL LIGHT+DARK SPARKLINES
// ============================================================================
// 🔥 V18: isDark pasado a FinanceHeader y FinanceTable (modo claro/oscuro completo)
// 🔥 V17.1: Soporte Light/Dark mode vía ThemeContext → isDark prop a SummaryCards
// 🔥 V17: Sparklines integrados en SummaryCards (receitas, despesas, saldo, vendas, aluguéis)
// 🔥 V17: Destructuración completa de useFinanceStats V7 con sparklines
// 🔥 V17: Props de sparkline pasadas a SummaryCards para mini gráficos diarios
// 🔥 V17: Ultra rápido — useMemo en hook, cero re-renders innecesarios
// 🔥 V17: Ultra preciso — safeNum, safeDivide, NaN-proof en todo el pipeline
// 🔥 V16: DataContext ASYNC — getContext espera la instancia correctamente
// 🔥 V16: ErrorBoundary para evitar crashes catastróficos
// ============================================================================

import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { useAuth } from "../contexts/useAuth";
import { useNotifications } from "../contexts/NotificationsContext";
import { useIsMobile } from "../hooks/useMediaQuery";
import { useTheme } from "../contexts/ThemeContext"; // 🆕 V17.1: Tema claro/oscuro
import FinanceCharts from "../components/finance/FinanceCharts";

// 🔥 V16: Importar DataContext como fuente única de verdad
import DataContext from "../lib/data/context/DataContext";

// Importar nossos hooks e componentes
import { useFinanceData } from "./hooks/useFinanceData";
import { useFinanceStats } from "./hooks/useFinanceStats";
import { useFinanceActions } from "./hooks/useFinanceActions";
import FinanceHeader from "./components/FinanceHeader";
import SummaryCards from "./components/SummaryCards";
import FinanceTable from "./components/FinanceTable";
import FinanceModal from "./components/FinanceModal";
import ConfirmDeleteModal from "./components/ConfirmDeleteModal";

// ============================================================================
// 🛡️ ERROR BOUNDARY (evita crashes catastróficos)
// ============================================================================
class FinanceErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('❌ [Financeiro] Error catastrófico:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-6 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-2xl mb-4">
            <span className="text-2xl">⚠️</span>
          </div>
          <h2 className="text-lg font-semibold text-primary mb-2">
            Erro no módulo financeiro
          </h2>
          <p className="text-sm text-muted mb-4">
            Ocorreu um erro inesperado. Tente recarregar a página.
          </p>
          <button
            onClick={() => {
              this.setState({ hasError: false, error: null });
              window.location.reload();
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors"
          >
            Recarregar página
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// ============================================================================
// 🎯 COMPONENTE PRINCIPAL
// ============================================================================
const FinanceiroPage = () => {
  // ==========================================================================
  // 🎯 CONTEXTO
  // ==========================================================================
  const { database, user, company } = useAuth();
  const isMobile = useIsMobile();
  const { isDark } = useTheme(); // 🆕 V17.1: Detectar tema claro/oscuro
  const { showToast, addNotification } = useNotifications();

  // 🔥 V16: Estado de inicialización de DataContext
  const [ctxReady, setCtxReady] = useState(false);
  const ctxRef = useRef(null);
  const initAttemptedRef = useRef(false);

  // 🔥 V16: getContext ASYNC — espera la instancia de DataContext
  const getContext = useCallback(async () => {
    if (ctxRef.current?.collections) {
      return ctxRef.current;
    }

    const userId = user?.uid || 
      (() => {
        try {
          const stored = localStorage.getItem('auth_user');
          return stored ? JSON.parse(stored).id : null;
        } catch {
          return null;
        }
      })();

    if (!userId) return null;

    try {
      const ctx = await DataContext.getInstance(userId);
      if (ctx?.collections) {
        ctxRef.current = ctx;
        if (!ctxReady) setCtxReady(true);
        return ctx;
      }
    } catch (error) {
      console.error('❌ [V16] Error obteniendo DataContext:', error.message);
    }

    return null;
  }, [user?.uid, ctxReady]);

  // 🔥 V16: Inicializar DataContext al montar (una sola vez)
  useEffect(() => {
    if (initAttemptedRef.current) return;
    initAttemptedRef.current = true;

    getContext().then(ctx => {
      if (ctx) {
        console.log('✅ [V16] FinanceiroPage: DataContext inicializado');
      } else {
        console.warn('⚠️ [V16] FinanceiroPage: DataContext no disponible, usando fallback');
        setCtxReady(true);
      }
    }).catch(() => {
      console.warn('⚠️ [V16] FinanceiroPage: Error init DataContext, usando fallback');
      setCtxReady(true);
    });
  }, [getContext]);

  // ==========================================================================
  // 🎣 HOOKS PERSONALIZADOS
  // ==========================================================================
  
  const { 
    registros, 
    setRegistros, 
    loading: dataLoading, 
    mes, 
    setMes, 
    handleRefresh,
    notifyLocalChange,
    getDataContext: dataGetContext
  } = useFinanceData(database, showToast, addNotification, getContext);

  const [filtroTipo, setFiltroTipo] = useState('todos');

  const { 
    registrosFiltrados, 
    vendasStats, 
    alugueisStats, 
    totalReceitas, 
    totalDespesas, 
    saldo,
    tendencia,
    resumoMensal,
    totaisGerais,
    receitasSparkline,
    despesasSparkline,
    saldoSparkline,
    vendasSparkline,
    alugueisSparkline,
  } = useFinanceStats(registros, mes, filtroTipo);

  const { 
    showModal, 
    setShowModal, 
    confirmDelete, 
    form, 
    setForm, 
    handleSave, 
    handleDeleteClick, 
    executeDelete, 
    cancelDelete, 
    handleExportPDF,
    handleShareWhatsApp,
    handleExportSinglePDF,
    refreshFinanceData
  } = useFinanceActions(
    database, 
    setRegistros, 
    company, 
    user, 
    showToast, 
    addNotification,
    notifyLocalChange,
    getContext
  );

  // ==========================================================================
  // 🔄 ESTADO DE CARGA COMBINADO
  // ==========================================================================
  const loading = dataLoading || (!ctxReady && !database);

  // ==========================================================================
  // 🛡️ SEGURANÇA
  // ==========================================================================
  const hasDataSource = database || ctxRef.current;

  // ==========================================================================
  // 🎨 RENDERIZAÇÃO
  // ==========================================================================
  
  if (!ctxReady && !database) {
    return (
      <div className={`${isMobile ? 'p-4' : 'p-6'} flex items-center justify-center min-h-[400px]`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto mb-4" />
          <p className={`text-slate-500 ${isMobile ? 'text-sm' : ''}`}>
            Inicializando módulo financeiro...
          </p>
          <p className="text-xs text-slate-400 mt-2">
            {user?.uid ? 'Conectando ao banco de dados...' : 'Verificando autenticação...'}
          </p>
        </div>
      </div>
    );
  }

  if (!hasDataSource) {
    return (
      <div className={`${isMobile ? 'p-4' : 'p-6'} text-gray-500 flex items-center justify-center min-h-[400px]`}>
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-100 rounded-2xl mb-4">
            <span className="text-2xl">📊</span>
          </div>
          <h2 className={`font-semibold text-slate-900 mb-2 ${isMobile ? 'text-base' : 'text-lg'}`}>
            Módulo financeiro indisponível
          </h2>
          <p className={`text-slate-500 mb-4 ${isMobile ? 'text-xs' : 'text-sm'}`}>
            Não foi possível conectar ao banco de dados.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  // ==========================================================================
  // 🎨 RENDERIZAÇÃO PRINCIPAL
  // ==========================================================================
  return (
    <>
      <ConfirmDeleteModal
        isOpen={confirmDelete.isOpen}
        title="Confirmar exclusão"
        message="Tem certeza que deseja excluir este lançamento?"
        onConfirm={executeDelete}
        onCancel={cancelDelete}
        isMobile={isMobile}
      />

      <div className={isMobile ? "p-3 max-w-7xl mx-auto" : "p-6 max-w-7xl mx-auto"}>
        
        {/* 🔥 V18: isDark pasado a FinanceHeader */}
        <FinanceHeader
          company={company}
          isMobile={isMobile}
          isDark={isDark}
          mes={mes}
          setMes={setMes}
          filtroTipo={filtroTipo}
          setFiltroTipo={setFiltroTipo}
          onRefresh={handleRefresh}
          onNewClick={() => setShowModal(true)}
          onExportPDF={() => handleExportPDF({
            mes,
            registrosFiltrados,
            totalReceitas,
            totalDespesas,
            saldo,
            vendasStats,
            alugueisStats
          })}
          loading={loading}
        />

        <SummaryCards
          isMobile={isMobile}
          isDark={isDark}
          registrosFiltrados={registrosFiltrados}
          totalReceitas={totalReceitas}
          totalDespesas={totalDespesas}
          saldo={saldo}
          vendasStats={vendasStats}
          alugueisStats={alugueisStats}
          totaisGerais={totaisGerais}
          tendencia={tendencia}
          receitasSparkline={receitasSparkline}
          despesasSparkline={despesasSparkline}
          saldoSparkline={saldoSparkline}
          vendasSparkline={vendasSparkline}
          alugueisSparkline={alugueisSparkline}
        />

        {/* 🔥 V19: Gráficos en móvil + desktop con toggle de detalles + isDark */}
        <div id="finance-charts-pdf" className={isMobile ? "mb-5" : "mb-8"}>
          <FinanceCharts
            totalReceitas={totalReceitas}
            totalDespesas={totalDespesas}
            registros={registrosFiltrados}
            vendas={vendasStats}
            alugueis={alugueisStats}
            isDark={isDark}
            defaultExpanded={!isMobile}
          />
        </div>

        {/* 🔥 V18: isDark pasado a FinanceTable */}
        <FinanceTable
          isMobile={isMobile}
          isDark={isDark}
          loading={loading}
          registrosFiltrados={registrosFiltrados}
          onDeleteClick={handleDeleteClick}
          onShareClick={handleShareWhatsApp}
          onExportSinglePDF={handleExportSinglePDF}
          onNewClick={() => setShowModal(true)}
        />

        <FinanceModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          onSave={handleSave}
          form={form}
          setForm={setForm}
          isMobile={isMobile}
        />
      </div>
    </>
  );
};

// ============================================================================
// 🎯 EXPORT CON ERROR BOUNDARY
// ============================================================================
const FinanceiroPageWithErrorBoundary = () => (
  <FinanceErrorBoundary>
    <FinanceiroPage />
  </FinanceErrorBoundary>
);

export default FinanceiroPageWithErrorBoundary;